// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/FichasTecnicas.tsx
//  Ficha Técnica / BOM versionada (paridade desktop FrmCadFichaTecnica).
//  Cadastro de fórmulas de produção (produto acabado + insumos) com histórico de versões.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, BookOpen, Trash2, Save, Eye, History, Pencil } from 'lucide-react';

import {
  fichaTecnicaService,
  type FichaTecnicaResumo,
  type FichaTecnicaItemRequest,
} from '@/features/operacional/services/fichaTecnicaService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';

const brl = (v: number) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDataHora = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('pt-BR');
};

interface ItemState extends FichaTecnicaItemRequest {
  _key: number;
}

// ── Página ──────────────────────────────────────────────────────────────────

export default function FichasTecnicas() {
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen]     = useState(false);
  const [editId, setEditId]         = useState<number | null>(null);
  const [detalheId, setDetalheId]   = useState<number | null>(null);

  // ── Estado do formulário ──────────────────────────────────────────────
  const [produtoId, setProdutoId]     = useState<number | undefined>(undefined);
  const [descricao, setDescricao]     = useState('');
  const [referencia, setReferencia]   = useState('');
  const [quantidade, setQuantidade]   = useState(1);
  const [perda, setPerda]             = useState(0);
  const [observacao, setObservacao]   = useState('');
  const [itens, setItens]             = useState<ItemState[]>([]);
  const [nextKey, setNextKey]         = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['fichas-tecnicas'],
    queryFn: () => fichaTecnicaService.getAll(1, 100),
  });
  const fichas = data?.itens ?? [];

  const resetForm = () => {
    setProdutoId(undefined); setDescricao(''); setReferencia('');
    setQuantidade(1); setPerda(0); setObservacao(''); setItens([]); setNextKey(1);
    setEditId(null);
  };

  const addItem = () =>
    setNextKey((k) => {
      setItens((p) => [...p, { _key: k, materiaPrimaId: 0, quantidade: 1, custoUnitario: 0 }]);
      return k + 1;
    });
  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));
  const updItem = (key: number, field: keyof FichaTecnicaItemRequest, value: number) =>
    setItens((p) => p.map((r) => (r._key === key ? { ...r, [field]: value } : r)));

  const abrirNovo = () => { resetForm(); setFormOpen(true); };

  const abrirEdicao = async (id: number) => {
    try {
      const f = await fichaTecnicaService.getById(id);
      setEditId(f.id);
      setProdutoId(f.produtoId);
      setDescricao(f.descricao ?? '');
      setReferencia(f.referencia ?? '');
      setQuantidade(f.quantidade);
      setPerda(f.perTotalPerda);
      setObservacao(f.observacao ?? '');
      setItens(f.itens.map((it, idx) => ({
        _key: idx + 1,
        materiaPrimaId: it.materiaPrimaId,
        quantidade: it.quantidade,
        custoUnitario: it.custoUnitario,
      })));
      setNextKey(f.itens.length + 1);
      setFormOpen(true);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!produtoId) throw new Error('Selecione o produto acabado');
      const itensValidos = itens.filter((i) => i.materiaPrimaId > 0 && i.quantidade > 0);
      if (itensValidos.length === 0) throw new Error('Adicione ao menos um insumo');
      const payload = {
        produtoId,
        quantidade,
        descricao: descricao || undefined,
        referencia: referencia || undefined,
        perTotalPerda: perda,
        observacao: observacao || undefined,
        itens: itensValidos.map(({ materiaPrimaId, quantidade, custoUnitario }) => ({
          materiaPrimaId, quantidade, custoUnitario,
        })),
      };
      return editId ? fichaTecnicaService.update(editId, payload) : fichaTecnicaService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fichas-tecnicas'] });
      toast.success(editId ? 'Ficha técnica atualizada (nova versão registrada)' : 'Ficha técnica criada');
      setFormOpen(false);
      resetForm();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => fichaTecnicaService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fichas-tecnicas'] });
      toast.success('Ficha técnica removida');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const { data: detalhe } = useQuery({
    queryKey: ['ficha-tecnica-detalhe', detalheId],
    queryFn: () => fichaTecnicaService.getById(detalheId!),
    enabled: detalheId != null,
  });

  const custoFormulario = useMemo(
    () => itens.reduce((s, i) => s + (i.quantidade || 0) * (i.custoUnitario || 0), 0),
    [itens],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Ficha Técnica / BOM</h1>
            <p className="text-sm text-muted-foreground">
              Fórmulas de produção versionadas — produto acabado e seus insumos.
            </p>
          </div>
        </div>
        <Button className="gap-2" onClick={abrirNovo}>
          <Plus className="h-4 w-4" />Nova Ficha
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Carregando...</div>
          ) : fichas.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhuma ficha técnica cadastrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto ID</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead className="text-right">Lote</TableHead>
                  <TableHead className="text-right">Insumos</TableHead>
                  <TableHead className="text-right">Custo Produção</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fichas.map((f: FichaTecnicaResumo) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">{f.produtoId}</TableCell>
                    <TableCell>{f.descricao ?? '—'}</TableCell>
                    <TableCell>{f.referencia ?? '—'}</TableCell>
                    <TableCell className="text-right">{f.quantidade}</TableCell>
                    <TableCell className="text-right">{f.totalItens}</TableCell>
                    <TableCell className="text-right">{brl(f.custoTotalProducao)}</TableCell>
                    <TableCell>
                      {f.isInativo
                        ? <Badge className="bg-red-500/10 text-red-500">Inativa</Badge>
                        : <Badge className="bg-green-500/10 text-green-500">Ativa</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetalheId(f.id)} title="Ver detalhe / versões">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => abrirEdicao(f.id)} title="Editar">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (confirm('Remover esta ficha técnica?')) removeMutation.mutate(f.id);
                      }} title="Remover">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Form (criar/editar) ──────────────────────────────────────────── */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar Ficha Técnica' : 'Nova Ficha Técnica'}</DialogTitle>
            <DialogDescription>
              Defina o produto acabado, a quantidade do lote de produção e os insumos. Cada
              salvamento registra uma nova versão no histórico.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Produto Acabado</Label>
              <ProdutoSearchCombobox
                value={produtoId}
                onChange={setProdutoId}
                placeholder="Selecione o produto acabado..."
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Nome da fórmula" />
            </div>
            <div className="space-y-2">
              <Label>Referência</Label>
              <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Código" />
            </div>
            <div className="space-y-2">
              <Label>Quantidade do Lote</Label>
              <Input type="number" min={0} step="any" value={quantidade}
                onChange={(e) => setQuantidade(Number(e.target.value))} />
              <p className="text-xs text-muted-foreground">
                Unidades produzidas por este lote (base do rateio de consumo).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Perda Total (%)</Label>
              <Input type="number" min={0} step="any" value={perda}
                onChange={(e) => setPerda(Number(e.target.value))} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Insumos / Matérias-primas</Label>
              <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
                <Plus className="h-4 w-4" />Adicionar Insumo
              </Button>
            </div>
            {itens.length === 0 ? (
              <p className="text-center py-6 text-sm text-muted-foreground border rounded-md">
                Nenhum insumo. Clique em "Adicionar Insumo".
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px]">Matéria-prima</TableHead>
                    <TableHead className="text-right">Qtd.</TableHead>
                    <TableHead className="text-right">Custo Unit.</TableHead>
                    <TableHead className="text-right">Custo Total</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((i) => (
                    <TableRow key={i._key}>
                      <TableCell>
                        <ProdutoSearchCombobox
                          value={i.materiaPrimaId || undefined}
                          onChange={(id) => updItem(i._key, 'materiaPrimaId', id)}
                          placeholder="Buscar insumo..."
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min={0} step="any" className="w-24 text-right ml-auto"
                          value={i.quantidade}
                          onChange={(e) => updItem(i._key, 'quantidade', Number(e.target.value))} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min={0} step="any" className="w-28 text-right ml-auto"
                          value={i.custoUnitario ?? 0}
                          onChange={(e) => updItem(i._key, 'custoUnitario', Number(e.target.value))} />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {brl((i.quantidade || 0) * (i.custoUnitario ?? 0))}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(i._key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-medium text-muted-foreground">
                      Custo Total de Produção:
                    </TableCell>
                    <TableCell className="text-right font-bold">{brl(custoFormulario)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormOpen(false); resetForm(); }}>Cancelar</Button>
            <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4" />{saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detalhe + histórico de versões ───────────────────────────────── */}
      <Dialog open={detalheId != null} onOpenChange={(o) => { if (!o) setDetalheId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ficha Técnica — {detalhe?.descricao ?? `Produto ${detalhe?.produtoId ?? ''}`}</DialogTitle>
            <DialogDescription>Insumos e histórico de versões.</DialogDescription>
          </DialogHeader>

          {detalhe && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-muted-foreground">Lote</p><p className="font-medium">{detalhe.quantidade}</p></div>
                <div><p className="text-muted-foreground">Custo Produção</p><p className="font-medium">{brl(detalhe.custoTotalProducao)}</p></div>
                <div><p className="text-muted-foreground">Custo Unitário</p><p className="font-medium">{brl(detalhe.custoTotalUnitario)}</p></div>
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Insumos</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matéria-prima ID</TableHead>
                      <TableHead className="text-right">Qtd.</TableHead>
                      <TableHead className="text-right">Custo Total</TableHead>
                      <TableHead className="text-right">% Comp.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalhe.itens.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>{it.materiaPrimaId}</TableCell>
                        <TableCell className="text-right">{it.quantidade}</TableCell>
                        <TableCell className="text-right">{brl(it.custoTotal)}</TableCell>
                        <TableCell className="text-right">{it.percentualComposicao}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <p className="text-sm font-medium mb-1 flex items-center gap-1">
                  <History className="h-4 w-4" />Histórico de versões
                </p>
                {detalhe.historicos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem registros.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {detalhe.historicos.map((h) => (
                      <li key={h.id} className="flex justify-between border-b py-1">
                        <span>{h.descricao}</span>
                        <span className="text-muted-foreground whitespace-nowrap ml-2">{fmtDataHora(h.dtUltimaAtualizacao)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalheId(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
